// GENERATED by schemas/codegen/generate.py from schemas/events.yaml — DO NOT EDIT.
// The subscribable watch-event taxonomy: the one place the portal worker
// (src/watch/subscribe.ts) and app (pages/Watchlists.tsx) read it from.

export const EVENT_TYPES = [
  "accounts.filed",
  "share_capital.allotment",
  "share_capital.statement",
  "charges.created",
  "charges.satisfied",
  "company.status_changed",
  "company.name_changed",
  "insolvency.case",
  "status.administration",
  "status.liquidation",
  "status.cva",
  "status.receivership",
  "status.dissolved",
  "status.restored",
  "status.strike_off_proposed",
  "status.strike_off_discontinued",
  "status.sic_changed",
  "filing.accounts_overdue",
  "filing.cs_lapsed",
  "capital.new_round",
  "capital.up_round",
  "capital.down_round",
  "capital.flat_round",
  "capital.convertible_conversion",
  "capital.heavy_dilution",
  "accounts.turned_profit",
  "accounts.turned_loss",
  "accounts.negative_equity",
  "accounts.runway_breach",
  "accounts.revenue_inflection",
  "accounts.size_band_jump",
  "accounts.size_band_drop",
  "accounts.dormant_to_trading",
  "charges.floating_created",
  "charges.burst",
  "ownership.new_parent",
  "ownership.parent_changed",
  "ownership.new_subsidiary",
  "ownership.group_restructure",
  "composite.distress_cluster",
  "composite.growth_cluster",
  "composite.acquisition",
  "composite.wind_down_to_distribute",
  "composite.recapitalization",
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

// Human labels for every kind (UI).
export const EVENT_LABELS: Record<EventType, string> = {
  "accounts.filed": "Accounts filed",
  "share_capital.allotment": "Share allotment (SH01)",
  "share_capital.statement": "Statement of capital (CS01)",
  "charges.created": "Charge registered",
  "charges.satisfied": "Charge satisfied",
  "company.status_changed": "Status change",
  "company.name_changed": "Name change",
  "insolvency.case": "Insolvency case",
  "status.administration": "Entered administration",
  "status.liquidation": "Entered liquidation",
  "status.cva": "CVA approved",
  "status.receivership": "Receivership",
  "status.dissolved": "Dissolved",
  "status.restored": "Restored to register",
  "status.strike_off_proposed": "Strike-off proposed",
  "status.strike_off_discontinued": "Strike-off discontinued",
  "status.sic_changed": "SIC code change",
  "filing.accounts_overdue": "Accounts overdue",
  "filing.cs_lapsed": "Confirmation statement lapsed",
  "capital.new_round": "First priced round",
  "capital.up_round": "Up round",
  "capital.down_round": "Down round",
  "capital.flat_round": "Flat round",
  "capital.convertible_conversion": "Convertible conversion",
  "capital.heavy_dilution": "Heavy dilution",
  "accounts.turned_profit": "Turned profitable",
  "accounts.turned_loss": "Turned loss-making",
  "accounts.negative_equity": "Negative equity",
  "accounts.runway_breach": "Runway breach",
  "accounts.revenue_inflection": "Revenue inflection",
  "accounts.size_band_jump": "Size band jump",
  "accounts.size_band_drop": "Size band drop",
  "accounts.dormant_to_trading": "Dormant to trading",
  "charges.floating_created": "Floating charge registered",
  "charges.burst": "Charge burst",
  "ownership.new_parent": "New parent",
  "ownership.parent_changed": "Parent changed",
  "ownership.new_subsidiary": "New subsidiary",
  "ownership.group_restructure": "Group restructure",
  "composite.distress_cluster": "Distress cluster",
  "composite.growth_cluster": "Growth cluster",
  "composite.acquisition": "Acquisition pattern",
  "composite.wind_down_to_distribute": "Wind-down pattern",
  "composite.recapitalization": "Recapitalization pattern",
};

// One-line plain-English explanations for every kind (UI tooltips /
// glossary). Faithful to what the pipeline producers actually fire on.
export const EVENT_DESCRIPTIONS: Record<EventType, string> = {
  "accounts.filed": "New annual accounts were filed with the register.",
  "share_capital.allotment": "New shares were issued (form SH01) \u2014 any share issue, priced or not.",
  "share_capital.statement": "A fresh statement of capital was filed with the annual confirmation statement (CS01) \u2014 the company's share-class snapshot.",
  "charges.created": "New security was registered over company assets (a mortgage or debenture) \u2014 usually new secured borrowing.",
  "charges.satisfied": "A registered charge was released \u2014 secured debt paid off.",
  "company.status_changed": "The company's registered status changed (e.g. active \u2192 liquidation).",
  "company.name_changed": "The company registered a new legal name.",
  "insolvency.case": "A new or updated insolvency case \u2014 administration, liquidation or CVA, with case type and dates.",
  "status.administration": "Administrators were appointed \u2014 an insolvency procedure to rescue or sell the business.",
  "status.liquidation": "The company entered liquidation \u2014 being wound up, with assets sold to pay creditors.",
  "status.cva": "A company voluntary arrangement was approved \u2014 a formal agreement with creditors to repay debts over time while trading on.",
  "status.receivership": "A receiver was appointed, typically by a secured lender to recover its debt.",
  "status.dissolved": "The company was dissolved and removed from the register.",
  "status.restored": "A previously dissolved company was restored to the register.",
  "status.strike_off_proposed": "The registrar proposed to strike the company off (often for missed filings); dissolution follows unless challenged.",
  "status.strike_off_discontinued": "A proposed strike-off was withdrawn \u2014 the company stays on the register.",
  "status.sic_changed": "The company's declared industry classification (SIC codes) changed.",
  "filing.accounts_overdue": "Annual accounts passed their filing deadline without being filed.",
  "filing.cs_lapsed": "The annual confirmation statement is overdue.",
  "capital.new_round": "The company's first priced funding round \u2014 its first share issue at a premium over nominal value.",
  "capital.up_round": "A priced funding round at a higher share price than the previous round.",
  "capital.down_round": "A priced funding round at a lower share price than the previous round \u2014 often a valuation cut.",
  "capital.flat_round": "A priced funding round at roughly the previous round's share price (within \u00b15%).",
  "capital.convertible_conversion": "Convertible instruments (loan notes / ASAs / SAFEs) converted into shares \u2014 priced at the instrument's discount, not the market.",
  "capital.heavy_dilution": "A share issue amounting to 30%+ of the shares in issue after it \u2014 existing holders heavily diluted.",
  "accounts.turned_profit": "The latest accounts show a profit after a loss-making prior year.",
  "accounts.turned_loss": "The latest accounts show a loss after a profitable prior year.",
  "accounts.negative_equity": "Shareholders' equity turned negative \u2014 liabilities now exceed assets on the balance sheet.",
  "accounts.runway_breach": "A loss-making company's cash, at the burn rate its latest accounts imply, covers less than 12 months.",
  "accounts.revenue_inflection": "A sharp revenue move year-on-year \u2014 growth of +50% or more, or a fall of 30% or more.",
  "accounts.size_band_jump": "The company moved up an accounts size band (micro \u2192 small \u2192 medium \u2192 large) \u2014 a step change in scale.",
  "accounts.size_band_drop": "The company moved down an accounts size band (large \u2192 medium \u2192 small \u2192 micro) \u2014 a contraction in scale.",
  "accounts.dormant_to_trading": "A previously dormant company filed trading accounts \u2014 it has started doing business.",
  "charges.floating_created": "A floating charge was registered \u2014 security over the company's assets in general, common in venture debt and lender tightening.",
  "charges.burst": "3 or more new charges were registered within 90 days \u2014 borrowing ramping unusually fast.",
  "ownership.new_parent": "The company gained a parent that consolidates its accounts (from GLEIF relationship data).",
  "ownership.parent_changed": "The company's consolidating parent was replaced by another \u2014 a possible change of control.",
  "ownership.new_subsidiary": "The company started consolidating a new subsidiary.",
  "ownership.group_restructure": "The company gained several subsidiaries at once \u2014 a group reorganisation or acquisition spree.",
  "composite.distress_cluster": "Several distress signals close together \u2014 e.g. overdue accounts, a new floating charge, auditor or going-concern flags.",
  "composite.growth_cluster": "Several growth signals close together \u2014 e.g. an up round plus a size-band jump or a new subsidiary.",
  "composite.acquisition": "A new parent plus a name change close together \u2014 the classic just-been-acquired pattern.",
  "composite.wind_down_to_distribute": "Share buybacks and capital reductions close together \u2014 capital being returned ahead of a wind-down.",
  "composite.recapitalization": "A down round together with a new preferred share class or heavy dilution \u2014 a balance-sheet reset.",
};

// Which kinds each register's pipeline producers actually fire — a
// subscriber can only select a kind their register emits (the worker
// enforces this; the UI shows only these per selected source).
export const SOURCE_EVENTS = {
  "gb-coh": [
    "accounts.filed",
    "share_capital.allotment",
    "share_capital.statement",
    "charges.created",
    "charges.satisfied",
    "company.status_changed",
    "company.name_changed",
    "insolvency.case",
    "status.administration",
    "status.liquidation",
    "status.cva",
    "status.receivership",
    "status.dissolved",
    "status.restored",
    "status.strike_off_proposed",
    "status.strike_off_discontinued",
    "status.sic_changed",
    "filing.accounts_overdue",
    "filing.cs_lapsed",
    "capital.new_round",
    "capital.up_round",
    "capital.down_round",
    "capital.flat_round",
    "capital.convertible_conversion",
    "capital.heavy_dilution",
    "accounts.turned_profit",
    "accounts.turned_loss",
    "accounts.negative_equity",
    "accounts.runway_breach",
    "accounts.revenue_inflection",
    "accounts.size_band_jump",
    "accounts.size_band_drop",
    "accounts.dormant_to_trading",
    "charges.floating_created",
    "charges.burst",
    "ownership.new_parent",
    "ownership.parent_changed",
    "ownership.new_subsidiary",
    "ownership.group_restructure",
    "composite.distress_cluster",
    "composite.growth_cluster",
    "composite.acquisition",
    "composite.wind_down_to_distribute",
    "composite.recapitalization",
  ],
  "ee-rik": [
    "accounts.filed",
    "company.status_changed",
    "company.name_changed",
    "accounts.turned_profit",
    "accounts.turned_loss",
    "accounts.negative_equity",
    "accounts.runway_breach",
    "accounts.revenue_inflection",
    "ownership.new_parent",
    "ownership.parent_changed",
    "ownership.new_subsidiary",
    "ownership.group_restructure",
    "composite.distress_cluster",
    "composite.growth_cluster",
    "composite.acquisition",
    "composite.wind_down_to_distribute",
    "composite.recapitalization",
  ],
  "lei": [
    "accounts.filed",
    "company.status_changed",
    "company.name_changed",
    "accounts.turned_profit",
    "accounts.turned_loss",
    "accounts.negative_equity",
    "accounts.runway_breach",
    "accounts.revenue_inflection",
    "ownership.new_parent",
    "ownership.parent_changed",
    "ownership.new_subsidiary",
    "ownership.group_restructure",
    "composite.distress_cluster",
    "composite.growth_cluster",
    "composite.acquisition",
    "composite.wind_down_to_distribute",
    "composite.recapitalization",
  ],
} as const satisfies Record<string, readonly EventType[]>;

// Thematic display groups for the UI selector — group order is the
// render order; member order follows the canonical EVENT_TYPES order.
export const EVENT_GROUPS = [
  {
    id: "filings",
    label: "Accounts & filings",
    types: [
      "accounts.filed",
      "filing.accounts_overdue",
      "filing.cs_lapsed",
    ],
  },
  {
    id: "status",
    label: "Status & registry",
    types: [
      "company.status_changed",
      "company.name_changed",
      "status.dissolved",
      "status.restored",
      "status.strike_off_proposed",
      "status.strike_off_discontinued",
      "status.sic_changed",
    ],
  },
  {
    id: "distress",
    label: "Distress & insolvency",
    types: [
      "insolvency.case",
      "status.administration",
      "status.liquidation",
      "status.cva",
      "status.receivership",
    ],
  },
  {
    id: "capital",
    label: "Funding & share capital",
    types: [
      "share_capital.allotment",
      "share_capital.statement",
      "capital.new_round",
      "capital.up_round",
      "capital.down_round",
      "capital.flat_round",
      "capital.convertible_conversion",
      "capital.heavy_dilution",
    ],
  },
  {
    id: "health",
    label: "Financial health",
    types: [
      "accounts.turned_profit",
      "accounts.turned_loss",
      "accounts.negative_equity",
      "accounts.runway_breach",
      "accounts.revenue_inflection",
      "accounts.size_band_jump",
      "accounts.size_band_drop",
      "accounts.dormant_to_trading",
    ],
  },
  {
    id: "charges",
    label: "Secured debt",
    types: [
      "charges.created",
      "charges.satisfied",
      "charges.floating_created",
      "charges.burst",
    ],
  },
  {
    id: "ownership",
    label: "Ownership & M&A",
    types: [
      "ownership.new_parent",
      "ownership.parent_changed",
      "ownership.new_subsidiary",
      "ownership.group_restructure",
    ],
  },
  {
    id: "patterns",
    label: "Pattern alerts",
    types: [
      "composite.distress_cluster",
      "composite.growth_cluster",
      "composite.acquisition",
      "composite.wind_down_to_distribute",
      "composite.recapitalization",
    ],
  },
] as const satisfies readonly { id: string; label: string; types: readonly EventType[] }[];

// The full watch_subscribe MCP tool description (taxonomy prose and
// all): the worker imports this verbatim and the codegen embeds the
// same string in the generated MCP contract, so the two can't drift
// and no kind can be omitted or misdescribed.
export const WATCH_SUBSCRIBE_DESCRIPTION = "Create a watchlist that receives matching company-change events. Each subscription targets ONE company_id plus an array of event types from the canonical taxonomy \u2014 every kind with its meaning, grouped: [Accounts & filings] ``accounts.filed``: New annual accounts were filed with the register. ``filing.accounts_overdue``: Annual accounts passed their filing deadline without being filed. ``filing.cs_lapsed``: The annual confirmation statement is overdue. [Status & registry] ``company.status_changed``: The company's registered status changed (e.g. active \u2192 liquidation). ``company.name_changed``: The company registered a new legal name. ``status.dissolved``: The company was dissolved and removed from the register. ``status.restored``: A previously dissolved company was restored to the register. ``status.strike_off_proposed``: The registrar proposed to strike the company off (often for missed filings); dissolution follows unless challenged. ``status.strike_off_discontinued``: A proposed strike-off was withdrawn \u2014 the company stays on the register. ``status.sic_changed``: The company's declared industry classification (SIC codes) changed. [Distress & insolvency] ``insolvency.case``: A new or updated insolvency case \u2014 administration, liquidation or CVA, with case type and dates. ``status.administration``: Administrators were appointed \u2014 an insolvency procedure to rescue or sell the business. ``status.liquidation``: The company entered liquidation \u2014 being wound up, with assets sold to pay creditors. ``status.cva``: A company voluntary arrangement was approved \u2014 a formal agreement with creditors to repay debts over time while trading on. ``status.receivership``: A receiver was appointed, typically by a secured lender to recover its debt. [Funding & share capital] ``share_capital.allotment``: New shares were issued (form SH01) \u2014 any share issue, priced or not. ``share_capital.statement``: A fresh statement of capital was filed with the annual confirmation statement (CS01) \u2014 the company's share-class snapshot. ``capital.new_round``: The company's first priced funding round \u2014 its first share issue at a premium over nominal value. ``capital.up_round``: A priced funding round at a higher share price than the previous round. ``capital.down_round``: A priced funding round at a lower share price than the previous round \u2014 often a valuation cut. ``capital.flat_round``: A priced funding round at roughly the previous round's share price (within \u00b15%). ``capital.convertible_conversion``: Convertible instruments (loan notes / ASAs / SAFEs) converted into shares \u2014 priced at the instrument's discount, not the market. ``capital.heavy_dilution``: A share issue amounting to 30%+ of the shares in issue after it \u2014 existing holders heavily diluted. [Financial health] ``accounts.turned_profit``: The latest accounts show a profit after a loss-making prior year. ``accounts.turned_loss``: The latest accounts show a loss after a profitable prior year. ``accounts.negative_equity``: Shareholders' equity turned negative \u2014 liabilities now exceed assets on the balance sheet. ``accounts.runway_breach``: A loss-making company's cash, at the burn rate its latest accounts imply, covers less than 12 months. ``accounts.revenue_inflection``: A sharp revenue move year-on-year \u2014 growth of +50% or more, or a fall of 30% or more. ``accounts.size_band_jump``: The company moved up an accounts size band (micro \u2192 small \u2192 medium \u2192 large) \u2014 a step change in scale. ``accounts.size_band_drop``: The company moved down an accounts size band (large \u2192 medium \u2192 small \u2192 micro) \u2014 a contraction in scale. ``accounts.dormant_to_trading``: A previously dormant company filed trading accounts \u2014 it has started doing business. [Secured debt] ``charges.created``: New security was registered over company assets (a mortgage or debenture) \u2014 usually new secured borrowing. ``charges.satisfied``: A registered charge was released \u2014 secured debt paid off. ``charges.floating_created``: A floating charge was registered \u2014 security over the company's assets in general, common in venture debt and lender tightening. ``charges.burst``: 3 or more new charges were registered within 90 days \u2014 borrowing ramping unusually fast. [Ownership & M&A] ``ownership.new_parent``: The company gained a parent that consolidates its accounts (from GLEIF relationship data). ``ownership.parent_changed``: The company's consolidating parent was replaced by another \u2014 a possible change of control. ``ownership.new_subsidiary``: The company started consolidating a new subsidiary. ``ownership.group_restructure``: The company gained several subsidiaries at once \u2014 a group reorganisation or acquisition spree. [Pattern alerts] ``composite.distress_cluster``: Several distress signals close together \u2014 e.g. overdue accounts, a new floating charge, auditor or going-concern flags. ``composite.growth_cluster``: Several growth signals close together \u2014 e.g. an up round plus a size-band jump or a new subsidiary. ``composite.acquisition``: A new parent plus a name change close together \u2014 the classic just-been-acquired pattern. ``composite.wind_down_to_distribute``: Share buybacks and capital reductions close together \u2014 capital being returned ahead of a wind-down. ``composite.recapitalization``: A down round together with a new preferred share class or heavy dilution \u2014 a balance-sheet reset. Set ``webhook_url`` (HTTPS only) for push delivery \u2014 omit it to receive events via ``watch_events`` polling. EMAIL delivery: set ``notify_email`` + ``email_cadence`` together (both or neither) to also receive events by email \u2014 webhook, polling and email compose on one watchlist. A repeat subscribe for a company you ALREADY watch edits that watchlist in place to match the request (the request is the desired end state \u2014 an omitted ``webhook_url`` or ``notify_email`` CLEARS that channel) rather than consuming a second slot. ``idempotency_key`` makes retries safe: a repeated call with the same key returns the original subscription. Returns the watchlist row including the canonical ``id`` to pass to ``watch_unsubscribe``. When ``webhook_url`` is set, the response ALSO includes ``webhook_secret`` (``whsec_`` + 64 hex) \u2014 RECORD IT NOW; it's shown only on the first successful create and cannot be retrieved later. Use it to verify the ``X-Plaingraph-Signature`` HMAC on incoming webhook POSTs (``POST /v1/watch/test`` \u2014 REST only, not an MCP tool \u2014 fires a synthetic signed delivery and returns verifier snippets; the ``X-Plaingraph-Event-Id`` request header is the idempotency key your handler MUST dedupe on). Lost the secret? Unsubscribe + re-subscribe to mint a fresh one (re-using the same ``idempotency_key`` after revocation is safe \u2014 slice 3a's partial unique index excludes revoked rows so the second call inserts cleanly). Bound by ``entitlement.watch_limit`` (default 10) \u2014 over the cap the call fails with 429 ``watch_limit_reached``. A separate rolling-30-day budget caps DISTINCT NEW companies entering the watched set (429 ``watch_add_rate_exceeded``, naming the numbers); re-watching a company you tracked before is free, and editing an existing watchlist never draws it.";

// Watchable registers with UI display metadata (the by-id selector);
// note is the register's coverage caveat shown under the selector.
export const REGISTERS = [
  {
    id: "gb-coh",
    label: "UK Companies House",
    exampleLocalId: "00006039",
    note: "UK companies are monitored within the hour from the live Companies House stream \u2014 the raw filings (accounts, share allotments, charges, status and name changes, new insolvency cases) arrive promptly. The derived kinds \u2014 status-lifecycle transitions (administration, liquidation, dissolution, strike-off), funding rounds, financial-health turns, filing-deadline flags, ownership changes and pattern alerts \u2014 are computed from those filings, GLEIF data and daily snapshots, so they land within a day.",
  },
  {
    id: "ee-rik",
    label: "Estonian e-Business Register",
    exampleLocalId: "12345678",
    note: "Estonian companies are checked daily \u2014 the register has no live stream. Accounts, status and name events come from the register; financial-health, ownership and pattern alerts are derived from filed accounts, GLEIF data and the event log.",
  },
  {
    id: "lei",
    label: "ESEF filer (EU/UK listed, by LEI)",
    exampleLocalId: "213800XJTHWG6Z7NEU24",
    note: "ESEF filers are checked daily. Annual financial reports drive the accounts and financial-health kinds; status and name changes are detected from the filing data; ownership and pattern alerts are derived from GLEIF data and the event log.",
  },
] as const;
